import DonorButton from '@/components/DonorButton';
import Logo from '@/components/Logo';
import NavigationItem from '@/components/NavigationItem';
import NavigationItemLabel from '@/components/NavigationItemLabel';
import NavigationNestedItem from '@/components/NavigationNestedItem';
import NavigationNestedList from '@/components/NavigationNestedList';
import Container from '@/components/Container';
import { NavigationItemHoverProvider } from '@/contexts/NavigationItemHoverContext';
import MobileMenu from './MobileMenu';

import { useRef } from 'react';
import NextLink from 'next/link';

import { Menu, X } from 'lucide-react';
import {
	useScrollPosition,
	useMobileMenu,
	useMenuFilterEffect,
} from './useHeader';

export default function Header() {
	const navItemRef1 = useRef<HTMLUListElement>(null);
	const navItemRef2 = useRef<HTMLUListElement>(null);
	const navItemRef3 = useRef<HTMLUListElement>(null);
	const navItemRef4 = useRef<HTMLUListElement>(null);
	const navItemRef5 = useRef<HTMLUListElement>(null);

	const isScrolled = useScrollPosition(80);
	const { isOpened: menuIsOpened, toggle: toggleMenu } = useMobileMenu();

	useMenuFilterEffect(menuIsOpened);

	return (
		<div className="header-wrapper">
			<Container>
				<header className="header">
					<div className="header-brand">
						<Logo />
						<DonorButton />
					</div>

					<nav
						className="header-navigation"
						aria-label="Menu de navegação"
						accessKey="m"
					>
						<ul className="header-navigation-menu">
							<NavigationItemHoverProvider ref={navItemRef1}>
								<NavigationItem>
									<NavigationItemLabel tabIndex={0}>
										Sobre Nós
									</NavigationItemLabel>
									<NavigationNestedList tooltip={{ direction: 'right' }}>
										<NavigationNestedItem>
											<NextLink
												aria-labelledby="#nossa-historia"
												href="/sobre-o-instituto/nossa-historia"
												tabIndex={0}
											>
												Conheça o Instituto
											</NextLink>
										</NavigationNestedItem>
										<NavigationNestedItem>
											<NextLink
												href="/sobre-o-instituto/por-que-existimos"
												tabIndex={0}
											>
												Por que existimos?
											</NextLink>
										</NavigationNestedItem>
										<NavigationNestedItem>
											<NextLink
												href="/sobre-o-instituto/quem-faz-acontecer"
												tabIndex={0}
											>
												Quem faz acontecer
											</NextLink>
										</NavigationNestedItem>
										<NavigationNestedItem>
											<NextLink href="/cartao-doador" tabIndex={0}>
												Cartão Doador
											</NextLink>
										</NavigationNestedItem>
										<NavigationNestedItem>
											<NextLink
												href="/sobre-o-instituto/transparencia"
												tabIndex={0}
											>
												Transparência
											</NextLink>
										</NavigationNestedItem>
									</NavigationNestedList>
								</NavigationItem>
							</NavigationItemHoverProvider>
							<NavigationItemHoverProvider ref={navItemRef2}>
								<NavigationItem>
									<NavigationItemLabel tabIndex={0}>
										O que fazemos
									</NavigationItemLabel>
									<NavigationNestedList tooltip={{ direction: 'right' }}>
										<NavigationNestedItem>
											<NextLink
												className="flex"
												href="/o-que-fazemos/educar-para-doar"
												tabIndex={0}
											>
												Informar, Inspirar e conscientizar: doação de órgãos
											</NextLink>
										</NavigationNestedItem>
										<NavigationNestedItem>
											<NextLink
												className="flex"
												href="/o-que-fazemos/saude-renal"
												tabIndex={0}
											>
												Saúde Renal: cuidado e prevenção
											</NextLink>
										</NavigationNestedItem>

										<NavigationNestedItem>
											<NextLink
												className="flex"
												href="/o-que-fazemos/rede-de-acolhimento"
												tabIndex={0}
											>
												Rede de Acolhimento: pacientes e cuidadores
											</NextLink>
										</NavigationNestedItem>
									</NavigationNestedList>
								</NavigationItem>
							</NavigationItemHoverProvider>
							<NavigationItemHoverProvider ref={navItemRef2}>
								<NavigationItem>
									<NavigationItemLabel tabIndex={0}>
										Podcast
									</NavigationItemLabel>
									<NavigationNestedList tooltip={{ direction: 'right' }}>
										<NavigationNestedItem>
											<NextLink
												className="flex"
												href="https://www.youtube.com/@deixevivo"
												target="_blank"
												tabIndex={0}
											>
												YouTube
											</NextLink>
										</NavigationNestedItem>
										<NavigationNestedItem>
											<NextLink
												className="flex"
												href="https://open.spotify.com/show/38absqTWyntQNAqSfxgq4l?si=3cf2d02bfdd3468b"
												target="_blank"
												tabIndex={0}
											>
												Spotify
											</NextLink>
										</NavigationNestedItem>
									</NavigationNestedList>
								</NavigationItem>
							</NavigationItemHoverProvider>
							<NavigationItemHoverProvider ref={navItemRef4}>
								<NavigationItem>
									<NavigationItemLabel>
										<NextLink
											href="https://blog.deixevivo.org.br"
											className="w-full h-full flex items-center"
											aria-label="Acesse nossa blog"
											target="_blank"
										>
											Blog
										</NextLink>
									</NavigationItemLabel>
								</NavigationItem>
							</NavigationItemHoverProvider>
							<NavigationItemHoverProvider ref={navItemRef5}>
								<NavigationItem>
									<NavigationItemLabel>
										<NextLink
											href="https://reserva.ink/deixevivo"
											className="w-full h-full flex items-center"
											aria-label="Acesse nossa loja virtual"
											target="_blank"
										>
											Loja Virtual
										</NextLink>
									</NavigationItemLabel>
								</NavigationItem>
							</NavigationItemHoverProvider>
						</ul>
					</nav>

					<div className="block lg:hidden">
						<button
							onClick={toggleMenu}
							className="button shadow-sm rounded-md"
							aria-label={
								!menuIsOpened
									? 'Abrir menu de navegação'
									: 'fechar menu de navegação'
							}
						>
							{!menuIsOpened ? (
								<Menu className="text-sm" />
							) : (
								<X className="text-sm animate-[spin_650ms_ease-in-out_1]" />
							)}
						</button>

						<div
							style={{ top: !isScrolled ? '3rem' : '4rem' }}
							className={`z-50 bg-white fixed left-0 w-[calc(100vw-5rem)] sm:w-[calc(100vw-7rem)] md:w-[calc(100vw-14rem)] h-screen shadow-xl transform transition-[transform] duration-500 translate-x-[-100vw] ${
								menuIsOpened
									? 'translate-x-[0px] visible'
									: 'translate-x-[-100vw] invisible'
							}`}
						>
							<ul className="bg-primary-dark flex flex-col h-full px-4 py-4 ">
								<MobileMenu />
							</ul>
						</div>
					</div>
				</header>
			</Container>
		</div>
	);
}
